import { useMutation } from "@apollo/client";
import { ToggleFeature } from "./types";
import { TOGGLE_FEATURE } from "./queries";
import { message } from "antd";
import { LOCATION_LEVEL_SECTIONS, SECTIONS } from '../../pages/cyber/constants';
import { ITApplicationsDashboard } from "../../pages/cyber";

type Stats = {
  [key in SECTIONS | LOCATION_LEVEL_SECTIONS]?: { count: number; isEnabled: boolean };
};

type UseHandleToggleFeatureProps = {
  refetch: () => Promise<{ data: ITApplicationsDashboard }>;
  setStats: React.Dispatch<React.SetStateAction<Stats>>;
}
/**
 * Custom hook to handle toggling feature flags in the cyber application.
 * 
 * This hook provides functionality to enable or disable specific features
 * such as application revenue mapping, process area and process tagging, business function tagging, and
 * qualitative factors. It handles the mutation, success/error messaging,
 * and updates the application state accordingly.
 * 
 * @param {Object} props - The hook properties
 * @param {Function} props.refetch - Function to refetch data after toggling a feature
 * @param {Function} props.setStats - State setter function to update the stats after toggling
 * @returns {Object} Object containing the toggle handler function and loading state
 */

export function useHandleToggleFeature({ refetch, setStats }: UseHandleToggleFeatureProps) {
  const [toggleFeature, { loading: toggleFeatureLoading }] =
  useMutation<ToggleFeature>(TOGGLE_FEATURE);
  
  const handleToggleFeature = async (flag: string, feature: string, isEnabled: boolean, locationUUID?: string) => {
    const result = await toggleFeature({ variables: { data: { flag, locationUUID, isEnabled } } });
    if (!result.errors?.length) {
      message.success(`${feature} mapping ${isEnabled ? 'disabled' : 'enabled'} successfully`);
      const { data } = await refetch();
      const { getItApplicationsDashboard: { cards } } = data;
      setStats(() => ({
        [SECTIONS.APPLICATION_REVENUE_MAPPING]: cards.applicationsMapped,
        [SECTIONS.BUSINESS_FUNCTION_TAGGING]: cards.businessFunctionsMapped,
        [SECTIONS.QUALITATIVE_FACTORS]: cards.qualitativeFactorsMapped,
        ...(locationUUID && { [LOCATION_LEVEL_SECTIONS.PROCESS_AREA_AND_PROCESS_TAGGING]: cards.processAreaMapped }),
      }));
    } else {
      message.error(`Failed to ${isEnabled ? 'disable' : 'enable'} ${feature} mapping`);
    }
  };

  return {
    handleToggleFeature,
    toggleFeatureLoading
  };
};